import { getViewport } from "features/map/GlobalViewState"

const earthCircumference = 40075017 // in meters

/**
 * Converts pixels to meters.
 *
 * @param pixels - The number of pixels to convert.
 * @param tileSize - The size of the tile (default is 512).
 * @returns The equivalent distance in meters.
 */
export function scaleUpFontSize(pixels: number, tileSize = 512): number {
    const zoomLevel = getViewport().zoom || 0
    const latitude = getViewport().latitude || 0
    // Convert latitude from degrees to radians
    const latRad = (latitude * Math.PI) / 180

    // Calculate meters per pixel, adjusted for latitude
    const metersPerPixel =
        (earthCircumference * Math.cos(latRad)) / (Math.pow(2, zoomLevel) * tileSize)

    // Convert pixels to meters
    const meters = metersPerPixel * pixels

    return meters
}

/**
 * Converts meters to pixels.
 *
 * @param meters - The distance in meters to convert.
 * @param tileSize - The size of the tile (default is 512).
 * @returns The equivalent distance in pixels.
 */
export function scaleDownFontSize(
    meters: number,
    tileSize = 512,
    latitude: number | undefined = undefined
): number {
    const view = getViewport()
    const zoomLevel = view.zoom || 0
    const lat = latitude ?? (view.latitude || 0)
    // Convert latitude from degrees to radians
    const latRad = (lat * Math.PI) / 180

    // Calculate meters per pixel, adjusted for latitude
    const metersPerPixel =
        (earthCircumference * Math.cos(latRad)) / (Math.pow(2, zoomLevel) * tileSize)

    // Convert meters to pixels
    const pixels = meters / metersPerPixel

    return pixels
}

/**
 * Calculates the zoom level needed to convert a given meter size to a specific font size in pixels.
 *
 * @param targetMeters - The target distance in meters.
 * @param targetPixels - The target font size in pixels.
 * @param tileSize - The size of the tile (default is 512).
 * @returns The calculated zoom level.
 */
export function calculateTextZoomLevel(
    targetMeters: number,
    targetPixels: number,
    latitude = 0,
    tileSize = 512
): number {
    const latRad = (latitude * Math.PI) / 180

    // Calculate the target meters per pixel
    const targetMetersPerPixel = targetMeters / targetPixels

    // Initialize zoom level
    let zoomLevel = 0

    // Iterate to find the zoom level
    while (zoomLevel <= 24) {
        const metersPerPixel =
            (earthCircumference * Math.cos(latRad)) / (Math.pow(2, zoomLevel) * tileSize)

        if (Math.abs(metersPerPixel - targetMetersPerPixel) < 0.0001) {
            return zoomLevel // Found the exact zoom level
        }

        if (metersPerPixel < targetMetersPerPixel) {
            break
        }

        zoomLevel++
    }

    return zoomLevel
}
